import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';
import userService from '../../../services/userService';
import useService from '../../../shared/hooks/useService';
import Role, { SpecialRole } from '../../../types/role';
import Context from '../../App/MainProvider/context';

import UIKitMenu from '../../../shared/UIKITComponents/Menu';
import UIKitMenuItem from '../../../shared/UIKITComponents/MenuItem';

const TopbarProfile: React.FC = () => {
  const { user, updateAsSpecialUserMode, inSuperUserMode, inGuidanceServiceMode } =
    useContext(Context);

  const navigate = useNavigate();
  const [image] = useService(userService.picture);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const canOpenMenu = useMemo(
    () => user.isSuperUser || user.isGuidanceService || user.roleCode === Role.Admin,
    [user.isSuperUser, user.isGuidanceService, user.roleCode]
  );

  const getSpecialRole = () => {
    if (inSuperUserMode()) {
      return '(Superuser)';
    }
    if (inGuidanceServiceMode()) {
      return '(Guidance Service)';
    }
    return '';
  };

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!canOpenMenu) {
      return;
    }
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  // Iniciales sin optional chaining
  // Iniciales (robusto ante null/undefined/no-string)
  let fullName = '';
  if (user && user.fullName !== null && typeof user.fullName !== 'undefined') {
    fullName = String(user.fullName);
  }

  const rawWords = fullName.split(' ');
  const words: string[] = [];
  for (let i = 0; i < rawWords.length; i += 1) {
    if (rawWords[i]) {
      words.push(rawWords[i]);
    }
  }

  let initials = '';
  if (words.length > 0) {
    initials += words[0].charAt(0);
  }
  if (words.length > 1) {
    initials += words[words.length - 1].charAt(0);
  }
  const initialAvatar = initials;

  const switchSpecialMode = (role: SpecialRole | null) => {
    updateAsSpecialUserMode(role);
    handleClose();
  };

  const navigateTo = (to: string) => {
    handleClose();
    navigate(to);
  };

  const menuItems = useMemo(() => {
    const items: Array<{ key: string; text: string; onClick: () => void }> = [];

    if (!canOpenMenu) {
      return items;
    }

    if (inSuperUserMode() || inGuidanceServiceMode()) {
      items.push({
        key: 'profile-original-user',
        text: user.fullName,
        onClick: () => switchSpecialMode(null),
      });
    }

    if (user.isSuperUser && !inSuperUserMode()) {
      items.push({
        key: 'profile-superuser',
        text: 'Superuser',
        onClick: () => switchSpecialMode(SpecialRole.Superuser),
      });
    }

    if (user.isGuidanceService && !inGuidanceServiceMode()) {
      items.push({
        key: 'profile-guidance',
        text: 'Guidance Service',
        onClick: () => switchSpecialMode(SpecialRole.GuidanceService),
      });
    }

    if (user.roleCode === Role.Admin) {
      items.push(
        {
          key: 'admin-general-settings',
          text: t('generalSettings'),
          onClick: () => navigateTo('/general-settings'),
        },
        {
          key: 'admin-roles-permissions',
          text: t('rolesAndPermissions'),
          onClick: () => navigateTo('/admin'),
        },
        {
          key: 'admin-api-logs',
          text: t('apiLogs'),
          onClick: () => navigateTo('/api-logs'),
        }
      );
    }

    return items;
  }, [
    canOpenMenu,
    inSuperUserMode,
    inGuidanceServiceMode,
    user.fullName,
    user.isSuperUser,
    user.isGuidanceService,
    user.roleCode,
    t,
  ]);
  if (!user) {
    return null;
  }
  return (
    <div
      className={`topbar__profile ${
        !user.isSuperUser && !user.isGuidanceService ? 'no-click' : ''
      }`}
    >
      <button
        type="button"
        className="topbar__avatar"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : 'false'}
      >
        {image ? (
          <img src={image} alt="avatar" className="topbar__avatar-img" />
        ) : (
          <svg height="34" width="34" style={{ margin: 'auto 0' }}>
            <circle cx="17" cy="17" r="16" stroke="white" strokeWidth="1" fill="#afbbca" />
            <text fill="#00325e" fontSize="16" fontWeight="600" textAnchor="middle" x="17" y="22">
              {initialAvatar}
            </text>
          </svg>
        )}

        <p className="topbar__avatar-name">
          <span>{user.fullName}</span>
          <span>{getSpecialRole()}</span>
        </p>
      </button>

      {canOpenMenu && open && anchorEl && (
        <UIKitMenu
          id="topbar-profile-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          scrollable={false}
        >
          <div onMouseLeave={handleClose}>
            {menuItems.map((item) => (
              <UIKitMenuItem
                className="topbar-submenu__item-height"
                key={item.key}
                text={item.text}
                onClick={item.onClick}
              />
            ))}
          </div>
        </UIKitMenu>
      )}
    </div>
  );
};

export default React.memo(TopbarProfile);
